import { CurrencyUtils } from '@dxp/utils'
import { ascend, clone, equals, groupBy, sort } from 'ramda'

import {
  DxpModelsAssetsDto,
  DxpModelsAssetSequenceDto,
  DxpModelsCashAssetDto,
  DxpModelsSecurityAssetDto,
  DxpModelsSecurityValuationDto,
} from '../generated/models/firm-user'

// refer to these files for Figaro/ODS > API mappings:
// dxp-lib/Repositories/AssetsRepository.cs:62
// https://github.com/QuilterPlc-EMU/figaro-ods/blob/main/API%20spec/openapi.yaml

// Sequence levels map the flat API fields to a hierarchical classification:
//   Level 0 (oneSequence/oneDescription)   = Asset class   (e.g. "Fixed Interest - UK", "Equities - UK")
//   Level 1 (twoSequence/twoDescription)   = Sector        (e.g. "Energy", "Financials")
//   Level 2 (threeSequence/threeDescription)= Sub-sector    (e.g. "Oil, Gas & Consumable Fuels", "Banks")
// A sequence value of 0 means the asset has no classification at that depth.

export const SEQUENCE_LEVELS = [
  { sequence: 'oneSequence', description: 'oneDescription' },
  { sequence: 'twoSequence', description: 'twoDescription' },
  { sequence: 'threeSequence', description: 'threeDescription' },
] as const satisfies readonly {
  sequence: AssetSequenceLevel
  description: AssetSequenceDescription
}[]

export type AccountAssetDetail = AssetDetail & {
  account: string
}

export type PortfolioAssetDetail = AssetDetail & {
  portfolio: string
}

export type AssetDetail = {
  sedol: string
  description: string
  quantity: number
  valuation: DxpModelsSecurityValuationDto
  sequence: DxpModelsAssetSequenceDto
  mpsAssets: {
    name: string
    percentage: number
    effectiveDate?: string
    value: number
  }[]
}

export type AssetSequenceLevel = 'oneSequence' | 'twoSequence' | 'threeSequence'
export type AssetSequenceDescription =
  | 'oneDescription'
  | 'twoDescription'
  | 'threeDescription'

export type AssetSequenceSector = {
  sequenceNumber: number | null
  description: string | null
  subSectors: AssetSequenceSector[]
  assets: DxpModelsSecurityAssetDto[]
  valuation?: AssetSequenceSectorValuation
}

export type AssetSequenceSectorValuation = {
  allocationPercentage: number
  bookCost: number
  income: number
  yield: number
  value: number
  currency: string
}

export type FormattedSectorValuation = {
  allocationPercentage: string
  bookCost: string
  income: string
  yield: string
  value: string
}

export const calculatePercentage = (
  numerator: number,
  denominator: number,
): number => (denominator !== 0 ? (numerator / denominator) * 100 : 0)

export const formatSectorValuation = (
  valuation: AssetSequenceSectorValuation,
): FormattedSectorValuation => ({
  allocationPercentage: `${valuation.allocationPercentage.toFixed(2)}%`,
  bookCost: CurrencyUtils.displayCurrency({
    currency: valuation.currency,
    amount: valuation.bookCost,
  }),
  income: CurrencyUtils.displayCurrency({
    currency: valuation.currency,
    amount: valuation.income,
  }),
  yield: `${valuation.yield.toFixed(2)}%`,
  value: CurrencyUtils.displayCurrencyWithISO({
    currency: valuation.currency,
    amount: valuation.value,
  }),
})

export const investmentsTableColumnType = Symbol('investmentsTableColumnType')

/* istanbul ignore next */
export const investmentsTableConfig: Record<
  string,
  {
    [investmentsTableColumnType]?: 'cash' | 'number' | 'percentage'
    label: string
    accessor: (cell: DxpModelsSecurityAssetDto) => unknown
  }
> = {
  description: {
    label: 'Asset name',
    accessor: row => row.description,
  },
  sedol: {
    label: '',
    accessor: row => row.sedol,
  },
  performance: {
    label: 'Gain/loss',
    [investmentsTableColumnType]: 'number',
    accessor: cell => cell.valuation.gainLossPercentage?.toFixed(2),
  },
  allocationPercentage: {
    label: 'Total',
    [investmentsTableColumnType]: 'number',
    accessor: cell => cell.valuation.allocationPercentage.toFixed(2),
  },
  quantity: {
    label: 'Nominal Units',
    [investmentsTableColumnType]: 'number',
    accessor: cell => cell.quantity,
  },
  bookCost: {
    label: 'Book cost',
    [investmentsTableColumnType]: 'cash',
    accessor: ({
      valuation: { bookCost: amount, bookCostCurrency: currency },
    }) => ({
      amount,
      currency,
    }),
  },
  unitPrice: {
    label: 'Unit price',
    [investmentsTableColumnType]: 'cash',
    accessor: ({ valuation: { price: amount, priceCurrency: currency } }) => ({
      amount,
      currency,
    }),
  },
  estimatedIncome: {
    label: 'Est. income',
    [investmentsTableColumnType]: 'cash',
    accessor: ({ valuation: { income: amount, currency } }) => ({
      amount,
      currency,
    }),
  },
  estimatedYield: {
    label: 'Est. yield',
    [investmentsTableColumnType]: 'number',
    accessor: cell =>
      cell.valuation?.yield === undefined || cell.valuation?.yield === 0
        ? '0.00'
        : cell.valuation?.yield.toFixed(2),
  },
  valuation: {
    label: 'Current value',
    [investmentsTableColumnType]: 'cash',
    accessor: ({ valuation: { value: amount, currency } }) => ({
      amount,
      currency,
    }),
  },
}

export const summarizeInvestments = (
  securities: DxpModelsSecurityAssetDto[],
) => {
  const summedValues = securities.reduce(
    (acc, { valuation }) => {
      const bookCost = acc.bookCost + valuation.bookCost
      const income = acc.income + valuation.income
      const value = acc.value + valuation.value
      return {
        bookCost,
        income,
        value,
      }
    },
    {
      bookCost: 0,
      income: 0,
      value: 0,
    },
  )

  return {
    bookCost: CurrencyUtils.displayCurrency({
      amount: summedValues.bookCost,
      currency: securities[0]?.valuation.bookCostCurrency ?? '',
    }),
    income: CurrencyUtils.displayCurrency({
      amount: summedValues.income,
      currency: securities[0]?.valuation.currency ?? '',
    }),
    value: CurrencyUtils.displayCurrency({
      amount: summedValues.value,
      currency: securities[0]?.valuation.currency ?? '',
    }),
    yield: `${calculatePercentage(summedValues.income, summedValues.value).toFixed(2)}%`,
  }
}

/* istanbul ignore next */
export const sortAssetsByAccount = sort<DxpModelsSecurityAssetDto>(
  ascend(asset => asset.description),
)

/* istanbul ignore next */
export const groupBySequenceLevel = (
  assets: (DxpModelsSecurityAssetDto | DxpModelsCashAssetDto)[],
  sequenceLevel: AssetSequenceLevel,
): Record<string, (DxpModelsCashAssetDto | DxpModelsSecurityAssetDto)[]> =>
  groupBy<DxpModelsSecurityAssetDto | DxpModelsCashAssetDto>(
    ({ sequence }) => `${sequence[sequenceLevel]}`,
    assets,
  ) as Record<string, (DxpModelsSecurityAssetDto | DxpModelsCashAssetDto)[]>

export const categorizeAssetsByOneSequence = (
  assets: (DxpModelsSecurityAssetDto | DxpModelsCashAssetDto)[],
): {
  sequence: number
  assetClass: string | null
  assets: (DxpModelsSecurityAssetDto | DxpModelsCashAssetDto)[]
}[] => {
  const oneSequenceMap = mapOneSequenceKeyValues(assets)

  const groupedBySequenceLevel = groupBySequenceLevel(
    orderAssetBySequence(assets, 'oneSequence'),
    'oneSequence',
  )

  return Object.keys(groupedBySequenceLevel).map(sequenceLevel => ({
    sequence: Number(sequenceLevel),
    assetClass: oneSequenceMap[sequenceLevel],
    assets: groupedBySequenceLevel[sequenceLevel] ?? [],
  }))
}

export const orderAssetBySequence = (
  assets: (DxpModelsSecurityAssetDto | DxpModelsCashAssetDto)[],
  sequenceLevel: AssetSequenceLevel,
) =>
  assets.sort(
    (
      a: DxpModelsSecurityAssetDto | DxpModelsCashAssetDto,
      b: DxpModelsSecurityAssetDto | DxpModelsCashAssetDto,
    ) => Number(a.sequence[sequenceLevel]) - Number(b.sequence[sequenceLevel]),
  )

export const mapOneSequenceKeyValues = (
  assets: (DxpModelsSecurityAssetDto | DxpModelsCashAssetDto)[],
) =>
  assets.reduce(
    (acc, { sequence }) => {
      acc[`${sequence.oneSequence}`] = sequence.oneDescription
      return acc
    },
    {} as Record<string, string | null>,
  )

export const orderBySequences = (
  assets: DxpModelsCashAssetDto[] | DxpModelsSecurityAssetDto[],
) =>
  orderAssetBySequence(
    orderAssetBySequence(
      orderAssetBySequence(assets, 'threeSequence'),
      'twoSequence',
    ),
    'oneSequence',
  )

export const createSector = (
  sequenceNumber: number | null,
  sequenceDescription: string | null,
): AssetSequenceSector => ({
  sequenceNumber,
  description: sequenceDescription,
  valuation: undefined,
  subSectors: [],
  assets: [],
})

export const filterAssetsWithSequencePattern = <
  T extends { sequence: DxpModelsAssetSequenceDto },
>(
  assets: T[],
  sequenceNumbers: (number | null)[],
): T[] => {
  const truthySequences = sequenceNumbers.filter(num => Number.isFinite(num))
  return assets.filter(
    ({ sequence: { oneSequence, twoSequence, threeSequence } }) => {
      const sequenceArr = [oneSequence, twoSequence, threeSequence]
      sequenceArr.length = truthySequences.length
      return equals(sequenceArr, truthySequences)
    },
  )
}

export const sumSecuritySectorValuations = (
  securities: DxpModelsSecurityAssetDto[] = [],
): AssetSequenceSectorValuation => {
  const summed = securities.reduce(
    (acc, { valuation }) => ({
      allocationPercentage:
        acc.allocationPercentage + valuation.allocationPercentage,
      bookCost: acc.bookCost + valuation.bookCost,
      income: acc.income + valuation.income,
      value: acc.value + valuation.value,
    }),
    {
      allocationPercentage: 0,
      bookCost: 0,
      income: 0,
      value: 0,
    },
  )

  const currency = securities[0]?.valuation?.currency ?? 'GBP'

  return {
    ...summed,
    yield: calculatePercentage(summed.income, summed.value),
    currency,
  }
}

export const sumCashSectorValuations = (
  cash: DxpModelsCashAssetDto[],
): AssetSequenceSectorValuation => {
  const summed = cash.reduce(
    (acc, { valuation }) => ({
      allocationPercentage:
        acc.allocationPercentage + valuation.allocationPercentage,
      bookCost: acc.bookCost + valuation.value,
      income: acc.income + valuation.valueCurrencyInc,
      value: acc.value + valuation.value,
    }),
    {
      allocationPercentage: 0,
      bookCost: 0,
      income: 0,
      value: 0,
    },
  )

  const currency = cash[0]?.valuation?.valueCurrency ?? 'GBP'

  return {
    ...summed,
    yield: calculatePercentage(summed.income, summed.value),
    currency,
  }
}

export const getMaxOneSequenceFromSectors = (
  sectors: AssetSequenceSector[],
): number =>
  sectors.reduce((num, { sequenceNumber }) => {
    return Math.max(num, Number(sequenceNumber))
  }, -1)

export const getMaxOneSequenceFromSecurities = (
  assets: DxpModelsSecurityAssetDto[],
): number =>
  assets.reduce((num, { sequence }) => {
    return Math.max(num, Number(sequence.oneSequence))
  }, -1)

export const fillInBlanks = (assets: DxpModelsSecurityAssetDto[]) => {
  const clonedAssets = clone(assets)
  const maxOneSec = getMaxOneSequenceFromSecurities(clonedAssets)

  return clonedAssets.map(asset => {
    const sequence = asset.sequence

    const isOther =
      sequence.oneSequence === null && sequence.oneDescription === null

    if (isOther) {
      sequence.oneSequence = maxOneSec + 1
      sequence.oneDescription = 'Others'
    }

    return {
      ...asset,
      sequence,
    }
  })
}

const sortAssetsByDescription = (
  a: { description?: string },
  b: { description?: string },
) => {
  const aDescription = a?.description ?? ''
  const bDescription = b?.description ?? ''
  return aDescription.localeCompare(bDescription)
}

/**
 * Recursively groups a pre-sorted list of securities into a sector tree.
 *
 * @param allAssets - The full sorted list (for filtering at each level)
 * @param depth - Current depth in the SEQUENCE_LEVELS config
 * @param maxDepth - How deep to nest (1 = shallow/flat, SEQUENCE_LEVELS.length = full)
 * @param parentPath - Sequence numbers of ancestor sectors
 */
const nestAssetsRecursive = (
  allAssets: DxpModelsSecurityAssetDto[],
  depth: number,
  maxDepth: number,
  parentPath: (number | null)[],
): AssetSequenceSector[] => {
  if (depth >= maxDepth) return []

  const level = SEQUENCE_LEVELS[depth]
  const sectors: AssetSequenceSector[] = []
  const seen = new Map<number | null, number>()

  const scopedAssets =
    parentPath.length > 0
      ? filterAssetsWithSequencePattern(allAssets, parentPath)
      : allAssets

  for (const asset of scopedAssets) {
    const seqNum = asset.sequence[level.sequence] as number | null

    // sequence 0 at this depth means the asset belongs to the parent, not a sub-sector
    if (depth > 0 && seqNum === 0) continue

    if (!seen.has(seqNum)) {
      seen.set(seqNum, sectors.length)
      sectors.push(
        createSector(
          seqNum,
          asset.sequence[level.description] as string | null,
        ),
      )
    }
  }

  for (const sector of sectors) {
    const currentPath = [...parentPath, sector.sequenceNumber]

    const matchingAssets = filterAssetsWithSequencePattern(
      allAssets,
      currentPath,
    )
    sector.valuation = sumSecuritySectorValuations(matchingAssets)

    if (depth < maxDepth - 1) {
      // assets at this level have 0 for the next sequence (leaf assets)
      const nextLevel = SEQUENCE_LEVELS[depth + 1]
      sector.assets = matchingAssets.filter(
        a => (a.sequence[nextLevel.sequence] as number) === 0,
      )
      sector.subSectors = nestAssetsRecursive(
        allAssets,
        depth + 1,
        maxDepth,
        currentPath,
      )
    } else {
      // at maxDepth, all matching assets are leaves
      sector.assets = [...matchingAssets].sort(sortAssetsByDescription)
      sector.subSectors = []
    }
  }

  return sectors
}

/**
 * Groups a list of securities by their sequence numbers and descriptions,
 * creating a hierarchical structure of asset sectors.
 *
 * @param assets - Securities to group
 * @param shallow - When true, groups only by the first sequence level (asset class).
 *   Equivalent to maxDepth=1. When false, groups to full depth.
 */
export const groupSecuritiesBySequence = (
  assets: DxpModelsSecurityAssetDto[],
  shallow = false,
): AssetSequenceSector[] => {
  if (!assets?.length) return []

  const filled = fillInBlanks(assets)
  const sortedSecurities = orderBySequences(
    filled,
  ) as DxpModelsSecurityAssetDto[]

  const maxDepth = shallow ? 1 : SEQUENCE_LEVELS.length

  return nestAssetsRecursive(sortedSecurities, 0, maxDepth, [])
}

export const getFooterTotals = ({
  securityAssetsDetails,
  cashAssetsDetails,
  combinedAssetDetails,
}: DxpModelsAssetsDto) => {
  const percentageTotal =
    securityAssetsDetails.percentage + cashAssetsDetails.percentage
  return {
    percentage: `${percentageTotal.toFixed(2)}%`,
    yield: `${combinedAssetDetails.yield.toFixed(2)}%`,
    value: CurrencyUtils.displayCurrency({
      amount: combinedAssetDetails.value,
      currency: combinedAssetDetails.currency,
    }),
    income: CurrencyUtils.displayCurrency({
      amount: combinedAssetDetails.income,
      currency: combinedAssetDetails.currency,
    }),
    bookCost: CurrencyUtils.displayCurrency({
      amount: combinedAssetDetails.bookCost,
      currency: combinedAssetDetails.currency,
    }),
  }
}
